import type { OnInit } from '@angular/core';
import { Component, ElementRef, forwardRef, Input, Renderer2 } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import type { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import {
    BaseMultiSelectFormFieldComponent
} from '@shared/form-fields/multi-select/base-multi-select-form-field.component';
import type { AppSelectOption } from '@shared/form-fields/options/select-option';
import { ApiParams } from '@shared/http/api-params';
import type { Role } from '@shared/modules/roles/role';
import { RoleFetcher } from '@shared/modules/roles/role.fetcher';

@Component({
    selector: 'app-role-multi-select',
    styleUrls: ['./role-multi-select.component.scss'],
    templateUrl: '../../../form-fields/multi-select/multi-select-form-field.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RoleMultiSelectComponent), multi: true }],
})
export class RoleMultiSelectComponent extends BaseMultiSelectFormFieldComponent<Role> implements OnInit {
    @Input() maxSelectedShowing = 1;
    @Input() params: ApiParams = {};
    @Input() onlyShowOfficeRoles = false;
    @Input() disableOptions: string[];
    @Input() willSelectAllWhenNoOptionsSelected = false;

    constructor(element: ElementRef,
                renderer: Renderer2,
                private fetcher: RoleFetcher) {
        super(element, renderer);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.fetcher.all({
            ...this.params,
            order_by: 'name',
            direction: 'asc',
        }).subscribe(response => {
            if (this.onlyShowOfficeRoles) {
                const excludeRoles = [
                    'carer',
                    'client',
                    'care_circle'
                ];

                response.data = response.data.filter(role => !excludeRoles.includes(role.id));
            }

            this.apiOptions = [...response.data.map(role => ({
                label: role.name,
                value: role
            }))];
        });
    }

    onSelectionChange($event: MatSelectChange) {
        super.onSelectionChange($event);

        const value: AppSelectOption<Role>[] = $event.value;

        if (value.length === 0 && this.willSelectAllWhenNoOptionsSelected) {
            this.selectedOptions = [...this.options];
        }
    }

    isOptionDisabled(option: AppSelectOption<Role>): boolean {
        if (this.disableOptions) {
            return this.disableOptions.includes(option.value.id);
        }

        return super.isOptionDisabled(option);
    }

    get selectedOptionsLabel(): string {
        if (this.allOptionsSelected) {
            return 'All Roles';
        }

        return super.selectedOptionsLabel;
    }

    get triggerLabel(): string {
        if (this.allOptionsSelected) {
            return `<span class="badge badge-small badge-outline">All Roles Selected</span>`;
        }

        return null;
    }
}
